import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import * as Feather from 'feather-icons';
import { LcStockListFilters } from '../logiciel-caisse-stock-list.types';

@Component({
  selector: 'app-logiciel-caisse-stock-toolbar',
  templateUrl: './logiciel-caisse-stock-toolbar.component.html',
  styleUrls: ['./logiciel-caisse-stock-toolbar.component.scss'],
})
export class LogicielCaisseStockToolbarComponent implements AfterViewInit, OnDestroy {
  @Input() searchPlaceholder =
    'Réf. fabricant, réf. interne, code-barres';
  @Input() warehouseSelectId = 'gestionStockWarehouseSelect';
  @Input() establishmentSelectId = 'gestionStockEstablishmentSelect';

  @Output() filtersChange = new EventEmitter<LcStockListFilters>();

  searchTerm = '';
  establishmentOptions: { id: number; label: string }[] = [];
  selectedEstablishmentIds: number[] = [];

  /** Tous les entrepôts de la station (non filtrés). */
  private allWarehouseOptions: { id: number; label: string }[] = [];
  /** Map établissement_id → entrepot_ids liés. */
  private entrepotIdsByEtablissement = new Map<number, number[]>();

  warehouseOptions: { id: number; label: string }[] = [];
  selectedWarehouseIds: number[] = [];

  showBoundsPanel = false;
  stockPhysMinInput: number | null = null;
  stockPhysMaxInput: number | null = null;
  private appliedStockPhysMin?: number;
  private appliedStockPhysMax?: number;

  private searchDebounce?: ReturnType<typeof setTimeout>;
  private filterDebounce?: ReturnType<typeof setTimeout>;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.refreshFeatherIcons();
    setTimeout(() => this.emitFilters(), 0);
  }

  ngOnDestroy(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    if (this.filterDebounce) {
      clearTimeout(this.filterDebounce);
    }
  }

  setEstablishmentOptions(options: { id: number; label: string }[]): void {
    this.establishmentOptions = options;
    this.cdr.detectChanges();
    this.refreshFeatherIcons();
  }

  setWarehouseOptions(options: { id: number; label: string }[]): void {
    this.allWarehouseOptions = options;
    this.refreshWarehouseOptions();
    this.cdr.detectChanges();
    this.refreshFeatherIcons();
  }

  setEntrepotIdsByEtablissement(map: Map<number, number[]>): void {
    this.entrepotIdsByEtablissement = map;
    this.refreshWarehouseOptions();
    this.pruneInvalidWarehouseSelection();
    this.cdr.detectChanges();
  }

  onSearch(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(() => this.emitFilters(), 300);
  }

  onEstablishmentChange(): void {
    this.refreshWarehouseOptions();
    this.pruneInvalidWarehouseSelection();
    this.scheduleFilterEmit();
  }

  onWarehouseChange(): void {
    this.scheduleFilterEmit();
  }

  applyBounds(): void {
    this.appliedStockPhysMin = this.parseBound(this.stockPhysMinInput);
    this.appliedStockPhysMax = this.parseBound(this.stockPhysMaxInput);
    this.emitFilters();
    this.refreshFeatherIcons();
  }

  resetBounds(): void {
    this.stockPhysMinInput = null;
    this.stockPhysMaxInput = null;
    this.appliedStockPhysMin = undefined;
    this.appliedStockPhysMax = undefined;
    this.emitFilters();
    this.refreshFeatherIcons();
  }

  getActiveFilters(): { key: string; label: string; value: string; optionValue?: string }[] {
    const filters: { key: string; label: string; value: string; optionValue?: string }[] = [];
    if (this.searchTerm.trim()) {
      filters.push({ key: 'search', label: 'Recherche', value: this.searchTerm.trim() });
    }
    for (const id of this.selectedEstablishmentIds) {
      const label = this.establishmentOptions.find((o) => o.id === id)?.label ?? String(id);
      filters.push({
        key: 'establishment',
        label: 'Établissement',
        value: label,
        optionValue: String(id),
      });
    }
    for (const id of this.selectedWarehouseIds) {
      const label =
        this.allWarehouseOptions.find((o) => o.id === id)?.label ??
        this.warehouseOptions.find((o) => o.id === id)?.label ??
        String(id);
      filters.push({
        key: 'warehouse',
        label: 'Entrepôt',
        value: label,
        optionValue: String(id),
      });
    }
    if (this.appliedStockPhysMin != null) {
      filters.push({
        key: 'stock_phys_min',
        label: 'Stock phys.',
        value: `≥ ${this.appliedStockPhysMin}`,
      });
    }
    if (this.appliedStockPhysMax != null) {
      filters.push({
        key: 'stock_phys_max',
        label: 'Stock phys.',
        value: `≤ ${this.appliedStockPhysMax}`,
      });
    }
    return filters;
  }

  trackByFilterKey(_index: number, filter: { key: string; optionValue?: string }): string {
    return filter.optionValue ? `${filter.key}-${filter.optionValue}` : filter.key;
  }

  removeFilter(key: string, optionValue?: string, event?: Event): void {
    event?.stopPropagation();
    if (key === 'search') {
      this.searchTerm = '';
    } else if (key === 'establishment') {
      if (optionValue) {
        const id = Number(optionValue);
        this.selectedEstablishmentIds = this.selectedEstablishmentIds.filter((w) => w !== id);
      } else {
        this.selectedEstablishmentIds = [];
      }
      this.refreshWarehouseOptions();
      this.pruneInvalidWarehouseSelection();
    } else if (key === 'warehouse') {
      if (optionValue) {
        const id = Number(optionValue);
        this.selectedWarehouseIds = this.selectedWarehouseIds.filter((w) => w !== id);
      } else {
        this.selectedWarehouseIds = [];
      }
    } else if (key === 'stock_phys_min') {
      this.stockPhysMinInput = null;
      this.appliedStockPhysMin = undefined;
    } else if (key === 'stock_phys_max') {
      this.stockPhysMaxInput = null;
      this.appliedStockPhysMax = undefined;
    }
    this.emitFilters();
    this.refreshFeatherIcons();
  }

  private scheduleFilterEmit(): void {
    if (this.filterDebounce) {
      clearTimeout(this.filterDebounce);
    }
    this.filterDebounce = setTimeout(() => {
      this.emitFilters();
      this.refreshFeatherIcons();
    }, 400);
  }

  private refreshWarehouseOptions(): void {
    if (this.selectedEstablishmentIds.length === 0) {
      this.warehouseOptions = [...this.allWarehouseOptions];
      return;
    }
    const allowed = new Set(this.getEntrepotIdsForSelectedEstablishments());
    this.warehouseOptions = this.allWarehouseOptions.filter((w) => allowed.has(w.id));
  }

  private pruneInvalidWarehouseSelection(): void {
    if (this.selectedEstablishmentIds.length === 0) {
      return;
    }
    const allowed = new Set(this.warehouseOptions.map((w) => w.id));
    this.selectedWarehouseIds = this.selectedWarehouseIds.filter((id) => allowed.has(id));
  }

  private getEntrepotIdsForSelectedEstablishments(): number[] {
    const ids = new Set<number>();
    for (const eid of this.selectedEstablishmentIds) {
      const list = this.entrepotIdsByEtablissement.get(eid) ?? [];
      for (const wid of list) {
        if (Number.isFinite(wid) && wid > 0) {
          ids.add(wid);
        }
      }
    }
    return Array.from(ids);
  }

  /**
   * Entrepôts effectifs envoyés à l’API :
   * - entrepôt seul : ses ids ;
   * - établissement seul : entrepôts liés ;
   * - les deux : intersection.
   */
  private computeEffectiveWarehouseIds(): number[] {
    const warehouseIds = this.selectedWarehouseIds.filter((n) => Number.isFinite(n) && n > 0);
    if (this.selectedEstablishmentIds.length === 0) {
      return warehouseIds;
    }
    const establishmentIds = this.getEntrepotIdsForSelectedEstablishments();
    if (warehouseIds.length === 0) {
      return establishmentIds;
    }
    const allowed = new Set(establishmentIds);
    return warehouseIds.filter((id) => allowed.has(id));
  }

  private emitFilters(): void {
    this.filtersChange.emit({
      searchTerm: this.searchTerm.trim(),
      establishmentIds: [...this.selectedEstablishmentIds],
      warehouseIds: [...this.selectedWarehouseIds],
      effectiveWarehouseIds: this.computeEffectiveWarehouseIds(),
      stockPhysMin: this.appliedStockPhysMin,
      stockPhysMax: this.appliedStockPhysMax,
    });
  }

  private parseBound(v: number | null): number | undefined {
    if (v == null || !Number.isFinite(v)) {
      return undefined;
    }
    return v;
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      try {
        Feather.replace();
      } catch {
        /* noop */
      }
      this.cdr.markForCheck();
    }, 0);
  }
}
